import { useMemo } from 'react';
import { getSpeakerLayoutBorderKey } from '@goldcast/utils';
import { CompositeSegment } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { layoutConfigMap } from '@goldcast/player-config/src/types/speaker-types';
import { roundToNDecimalPlaces } from '@goldcast/player-config/src/utils/mathUtils';
import { isLayoutAudiogram } from '@goldcast/player-config/src/utils/layout';

export const useCompositeSegment = ({ segment, currentFrame }: { segment: CompositeSegment; currentFrame: number }) => {
  const layoutDetailsAtCurrentFrame = segment.layoutChanges.find(change => {
    return currentFrame >= change.fromFrame && currentFrame <= change.toFrame;
  });

  const commonStartFromForElements = useMemo(() => {
    // Reference - https://www.remotion.dev/docs/miscellaneous/snippets/jumpcuts
    const firstVideo = segment.videos[0];
    if (!firstVideo) {
      return null;
    }

    const fromFrame = firstVideo.fromFrame;
    if (!segment.playRanges.length) {
      return fromFrame;
    }

    let summedUpDurations = 0;
    for (const section of segment.playRanges) {
      summedUpDurations += section.toFrame - section.fromFrame;
      if (summedUpDurations > currentFrame) {
        return roundToNDecimalPlaces(section.toFrame - summedUpDurations, 3);
      }
    }

    return fromFrame;
  }, [currentFrame]);

  const backgroundStyle = useMemo(() => {
    const background = segment.details.background;
    if (background.type === 'IMAGE_URL') {
      return { backgroundImage: `url(${background.value})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }

    // For type === 'COLOR' or others
    return { backgroundColor: background.value };
  }, [segment.details?.background?.value]);

  const videosToShow: string[] = useMemo(() => {
    if (!layoutDetailsAtCurrentFrame) {
      return [];
    }

    const videoIds = layoutDetailsAtCurrentFrame.videoIds;

    return segment.videos
      .map(video => video.id)
      .filter(videoId => videoIds.includes(videoId))
      .sort((a, b) => videoIds.indexOf(a) - videoIds.indexOf(b));
  }, [layoutDetailsAtCurrentFrame?.videoIds, layoutDetailsAtCurrentFrame?.layout, segment.videos]);

  // Pre-compute which layout changes introduce new videos (only recalculates when layout changes, not every frame)
  const layoutChangesWithNewVideos = useMemo(() => {
    const previousVideoIds = new Set<string>();
    const framesWithNewVideos: number[] = [];

    for (let i = 0; i < segment.layoutChanges.length; i++) {
      const layoutChange = segment.layoutChanges[i];

      const hasNewVideos = layoutChange.videoIds.some(id => !previousVideoIds.has(id));

      if (hasNewVideos) {
        framesWithNewVideos.push(layoutChange.fromFrame);
      }

      layoutChange.videoIds.forEach(id => previousVideoIds.add(id));
    }

    return framesWithNewVideos;
  }, [segment.layoutChanges]);

  // Find when the current speaker was introduced to restart their label animation from that frame
  const mostRecentLayoutChangeFrame = useMemo(() => {
    if (currentFrame === 0 || !layoutDetailsAtCurrentFrame) {
      return 0;
    }

    // Look backwards through layout changes to find the most recent one that added new videos
    for (let i = layoutChangesWithNewVideos.length - 1; i >= 0; i--) {
      const frame = layoutChangesWithNewVideos[i];
      if (frame <= currentFrame) {
        return frame;
      }
    }

    return 0;
  }, [currentFrame, layoutDetailsAtCurrentFrame, layoutChangesWithNewVideos]);

  // Use pre-computed playback-adjusted effects from player config
  const playableEffects = segment.playbackAdjustedEffects || [];

  const finalConfig = useMemo(() => {
    // If making changes here please update the getFinalConfigForLayout function in layoutUtils.ts
    if (!layoutDetailsAtCurrentFrame) {
      return [];
    }
    if (isLayoutAudiogram(layoutDetailsAtCurrentFrame.layout)) {
      return [];
    }
    const config =
      layoutConfigMap[layoutDetailsAtCurrentFrame.size]?.[layoutDetailsAtCurrentFrame.layout]?.[
        layoutDetailsAtCurrentFrame.fitMode
      ]?.[getSpeakerLayoutBorderKey(!layoutDetailsAtCurrentFrame.hideBorders)]?.[videosToShow.length];

    if (config) {
      return config;
    }
    return [
      {
        style: { display: 'none' },
        containerStyle: { display: 'none' }
      }
    ];
  }, [layoutDetailsAtCurrentFrame, videosToShow]);

  return {
    layoutDetailsAtCurrentFrame,
    commonStartFromForElements,
    backgroundStyle,
    mostRecentLayoutChangeFrame,
    playableEffects,
    finalConfig,
    videosToShow
  };
};
